import jsPDF from 'jspdf';
import { logger } from './logger';

// Max images fetched+decoded in parallel during PDF generation. Bounds peak
// memory/network so a large export (catalog ceiling is 650 items) doesn't fire
// hundreds of simultaneous fetches. Issue #1208 removed the former hard
// 50-item cap in favour of this concurrency-limited streaming approach.
const CONCURRENT_IMAGE_LOADS = 6;
const MARGIN = 20;
const PAGE_WIDTH = 210; // A4 width in mm
const PAGE_HEIGHT = 297; // A4 height in mm
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const IMAGE_MAX_WIDTH = 70;
const IMAGE_MAX_HEIGHT = 70;
// Cap the longest edge of each re-encoded image (px). Images only ever display
// in a ~70mm box (≈830px at 300dpi), so this preserves print quality while
// bounding the bytes RETAINED per image — the full prefetch holds every image
// at once, so without this a large export (#1208) could balloon browser memory.
const MAX_IMAGE_DIMENSION_PX = 1000;

/** Progress callback invoked during generation: (completed items, total items). */
export type PdfProgressCallback = (completed: number, total: number) => void;

export interface PdfExportItem {
  aliCatalogNumber: string;
  title: string;
  creators: string;
  creationDate: string;
  workType: string;
  techniques: string;
  materials: string;
  dimensions: string;
  keywords: string;
  publicNotes: string;
  privateNotes: string;
  physicalLocation: string;
  estimatedValue: string;
  provenanceRecords: string;
  editions: string;
  exhibitions: string;
  publications: string;
  awards: string;
  customCatalogNumbers: string;
  alternativeTitles: string;
  substrate: string;
  mediaDescription?: string;
  imageUrl?: string;
}

export interface PdfExportOptions {
  includePrivateFields: boolean;
}

interface ImageData {
  base64: string;
  width: number;
  height: number;
}

const loadImageAsBase64 = async (url: string): Promise<ImageData | null> => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        // Load image to get natural dimensions and re-encode to JPEG via canvas.
        // jsPDF's addImage is called with format='JPEG', so the bytes must be
        // genuine JPEG regardless of the source rendition format (AVIF/WebP/PNG).
        // The browser has already decoded the image into an Image element, so we
        // draw it onto an offscreen canvas and export as JPEG.
        const img = new Image();
        img.onload = () => {
          const naturalWidth = img.naturalWidth;
          const naturalHeight = img.naturalHeight;
          try {
            // Downscale to MAX_IMAGE_DIMENSION_PX on the longest edge (never
            // upscale), preserving aspect ratio. Bounds retained bytes per image.
            const longestEdge = Math.max(naturalWidth, naturalHeight);
            const scale = longestEdge > MAX_IMAGE_DIMENSION_PX
              ? MAX_IMAGE_DIMENSION_PX / longestEdge
              : 1;
            const targetWidth = Math.max(1, Math.round(naturalWidth * scale));
            const targetHeight = Math.max(1, Math.round(naturalHeight * scale));

            const canvas = document.createElement('canvas');
            canvas.width = targetWidth;
            canvas.height = targetHeight;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              resolve(null);
              return;
            }
            // Fill with white to handle images with transparency (JPEG has no alpha).
            ctx.fillStyle = '#fff';
            ctx.fillRect(0, 0, targetWidth, targetHeight);
            ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
            // Re-encode to JPEG. If the canvas is tainted (cross-origin without
            // CORS), toDataURL throws a SecurityError — catch and skip the image.
            const jpegDataUrl = canvas.toDataURL('image/jpeg', 0.92);
            // Report the scaled dimensions — aspect ratio is unchanged, so the
            // render-box fit (calculateImageDimensions) is identical.
            resolve({
              base64: jpegDataUrl,
              width: targetWidth,
              height: targetHeight,
            });
          } catch (canvasError) {
            logger.debug('Canvas re-encode failed for PDF image, skipping', canvasError);
            resolve(null);
          }
        };
        img.onerror = () => {
          resolve(null);
        };
        img.src = dataUrl;
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    logger.debug('Failed to load image for PDF export', error);
    return null;
  }
};

interface ImageDimensions {
  width: number;
  height: number;
}

const calculateImageDimensions = (
  naturalWidth: number,
  naturalHeight: number,
  maxWidth: number,
  maxHeight: number
): ImageDimensions => {
  if (!Number.isFinite(naturalWidth) || !Number.isFinite(naturalHeight) ||
      naturalWidth <= 0 || naturalHeight <= 0) {
    return { width: maxWidth, height: maxHeight };
  }

  // Calculate the scale factor to fit within the max box while preserving aspect ratio
  const scaleFactor = Math.min(maxWidth / naturalWidth, maxHeight / naturalHeight);
  return {
    width: naturalWidth * scaleFactor,
    height: naturalHeight * scaleFactor,
  };
};

/**
 * Fetch+decode all item images with bounded concurrency, preserving order.
 * Returns an array aligned to `items`; an entry is null when the item has no
 * image or its fetch/decode failed (those items still render their metadata).
 * Reports progress as each item completes — this is the slow (network) phase,
 * so it's where a progress indicator is meaningful.
 */
const prefetchImages = async (
  items: PdfExportItem[],
  onProgress?: PdfProgressCallback
): Promise<(ImageData | null)[]> => {
  const total = items.length;
  const results: (ImageData | null)[] = new Array(total).fill(null);
  let cursor = 0;
  let completed = 0;

  const worker = async (): Promise<void> => {
    // `cursor` read+increment is atomic between awaits (single-threaded JS),
    // so each worker claims a unique index.
    while (cursor < total) {
      const index = cursor;
      cursor += 1;
      const url = items[index].imageUrl;
      results[index] = url ? await loadImageAsBase64(url) : null;
      completed += 1;
      onProgress?.(completed, total);
    }
  };

  const poolSize = Math.min(CONCURRENT_IMAGE_LOADS, total);
  await Promise.all(Array.from({ length: poolSize }, () => worker()));
  return results;
};

export const generateCatalogPdf = async (
  items: PdfExportItem[],
  options: PdfExportOptions,
  onProgress?: PdfProgressCallback
): Promise<void> => {
  const doc = new jsPDF();
  // Fetch every image up front with bounded concurrency, then render pages in
  // order. Removes the former sequential per-page fetch bottleneck and the
  // hard 50-item cap (#1208) while keeping peak memory in check.
  const prefetchedImages = await prefetchImages(items, onProgress);

  let yPos = MARGIN;

  const checkPageBreak = (needed: number) => {
    if (yPos + needed > PAGE_HEIGHT - MARGIN) {
      doc.addPage();
      yPos = MARGIN;
    }
  };

  const addText = (text: string, x: number, size: number = 10) => {
    const lines = doc.splitTextToSize(text, CONTENT_WIDTH - (x - MARGIN));
    for (const line of lines) {
      checkPageBreak(size * 0.4);
      doc.text(line, x, yPos);
      yPos += size * 0.4;
    }
  };

  const addSection = (label: string, value: string) => {
    if (!value) return;
    checkPageBreak(12);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(label, MARGIN, yPos);
    yPos += 4;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(0);
    addText(value, MARGIN);
    yPos += 3;
  };

  for (let i = 0; i < items.length; i++) {
    const item = items[i];

    if (i > 0) {
      doc.addPage();
      yPos = MARGIN;
    }

    // Header: ALI Catalog Number + page number
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(item.aliCatalogNumber || 'No Catalog Number', MARGIN, yPos);

    const pageLabel = `Page ${i + 1}`;
    doc.text(pageLabel, PAGE_WIDTH - MARGIN - doc.getTextWidth(pageLabel), yPos);
    yPos += 8;

    // Image + metadata side by side
    let metadataX = MARGIN;
    let actualImageHeight = IMAGE_MAX_HEIGHT; // Track actual image height for page break calculations

    const imageData = prefetchedImages[i];
    if (imageData) {
      // Calculate dimensions that preserve aspect ratio
      const { width, height } = calculateImageDimensions(
        imageData.width,
        imageData.height,
        IMAGE_MAX_WIDTH,
        IMAGE_MAX_HEIGHT
      );
      actualImageHeight = height;
      doc.addImage(imageData.base64, 'JPEG', MARGIN, yPos, width, height);
      metadataX = MARGIN + width + 8;
    }

    const metadataStartY = yPos;

    // Title (italic)
    if (item.title) {
      doc.setFont('helvetica', 'bolditalic');
      doc.setFontSize(14);
      doc.setTextColor(0);
      doc.text(item.title, metadataX, yPos);
      yPos += 7;
    }

    // Creator + Date
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    const creatorLine = [item.creators, item.creationDate].filter(Boolean).join(', ');
    if (creatorLine) {
      doc.text(creatorLine, metadataX, yPos);
      yPos += 6;
    }

    // Medium (prefer mediaDescription, fall back to techniques + materials)
    const medium = item.mediaDescription?.trim() || [item.techniques, item.materials].filter(Boolean).join('; ');
    if (medium) {
      doc.setFontSize(10);
      doc.text(medium, metadataX, yPos);
      yPos += 5;
    }

    // Dimensions
    if (item.dimensions) {
      doc.text(item.dimensions, metadataX, yPos);
      yPos += 5;
    }

    // Work Type
    if (item.workType) {
      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text(item.workType, metadataX, yPos);
      yPos += 5;
    }

    // Ensure we're below the image if one was actually rendered (not just requested).
    // Gate on `imageData` (the prefetched result), NOT `item.imageUrl`: when fetch
    // fails the slot is null and no image was drawn, so reserving the full
    // IMAGE_MAX_HEIGHT would leave a blank 70mm gap. (#1208)
    if (imageData && yPos < metadataStartY + actualImageHeight + 5) {
      yPos = metadataStartY + actualImageHeight + 5;
    }

    yPos += 5;
    doc.setTextColor(0);

    // Inventory Numbers
    addSection('INVENTORY NUMBERS', item.customCatalogNumbers);

    // Alternative Titles
    addSection('ALTERNATIVE TITLES', item.alternativeTitles);

    // Substrate
    addSection('SUBSTRATE', item.substrate);

    // Editions
    addSection('EDITIONS', item.editions);

    // Provenance
    addSection('PROVENANCE', item.provenanceRecords);

    // Exhibitions
    addSection('EXHIBITIONS', item.exhibitions);

    // Publications
    addSection('PUBLICATIONS', item.publications);

    // Awards & Honors — issue #1007
    addSection('AWARDS & HONORS', item.awards);

    // Keywords
    addSection('KEYWORDS', item.keywords);

    // Public Notes
    addSection('NOTES', item.publicNotes);

    // Location (visible in all exports)
    addSection('PHYSICAL LOCATION', item.physicalLocation);

    // Private fields (conditional)
    if (options.includePrivateFields) {
      addSection('PRIVATE NOTES', item.privateNotes);
      addSection('ESTIMATED VALUE', item.estimatedValue);
    }
  }

  const timestamp = new Date().toISOString().split('T')[0];
  doc.save(`catalog-export-${timestamp}.pdf`);
};
